import { ClerkProvider } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './styles/globals.css';
import './styles/landing.css';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { AuthProvider } from './contexts/auth-context.tsx';
import { EncryptionProvider } from './contexts/EncryptionContext.tsx';

/**
 * Enhanced environment detection for client-side Sentry
 * Provides better context when standard environment variables are missing
 */
function getClientEnvironmentContext() {
  const sentryEnv = import.meta.env.VITE_SENTRY_ENVIRONMENT; // Explicit environment override
  const mode = import.meta.env.MODE;
  const dev = import.meta.env.DEV;
  const prod = import.meta.env.PROD;

  // Determine environment with explicit fallback handling
  // Priority: VITE_SENTRY_ENVIRONMENT > hostname detection > MODE
  let environment = 'unknown-browser';

  if (sentryEnv) {
    // Explicit VITE_SENTRY_ENVIRONMENT takes priority (staging vs production)
    environment = sentryEnv;
  } else if (window.location.hostname === 'staging.expertmd.io') {
    environment = 'staging';
  } else if (window.location.hostname === 'expertmd.io') {
    environment = 'production';
  } else if (window.location.hostname === 'localhost') {
    environment = 'local-development';
  } else if (window.location.hostname.includes('vercel.app')) {
    environment = 'vercel-preview';
  } else if (window.location.hostname.includes('railway.app')) {
    environment = 'railway-staging';
  } else if (mode) {
    environment = mode;
  } else if (dev) {
    environment = 'development';
  } else if (prod) {
    environment = 'production';
  }

  // Browser and platform context
  const browserContext = {
    user_agent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    online: navigator.onLine,
    hostname: window.location.hostname,
    protocol: window.location.protocol,
    has_mode: !!mode,
    has_dev: !!dev,
    has_prod: !!prod,
    deployment_context: environment === 'unknown-browser' ? 'build-time-issue' : 'runtime',
    available_env_vars: {
      MODE: !!mode,
      DEV: !!dev,
      PROD: !!prod,
      VITE_SENTRY_DSN: !!import.meta.env.VITE_SENTRY_DSN,
    },
  };

  return { environment, browserContext };
}

// Initialize Sentry for error tracking
// Only enabled in staging and production (disabled in development)
const isSentryEnabled = import.meta.env.VITE_SENTRY_DSN && import.meta.env.MODE !== 'development';

if (isSentryEnabled) {
  const { environment, browserContext } = getClientEnvironmentContext();

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of transactions for performance monitoring
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    environment,

    // Enhanced error context
    beforeSend(event, hint) {
      // Add browser context for all events
      event.contexts = {
        ...event.contexts,
        browser: browserContext,
      };

      // Add environment detection tags for unknown scenarios
      if (environment === 'unknown-browser') {
        event.tags = {
          ...event.tags,
          env_detection: 'failed',
          deployment_context: browserContext.deployment_context,
        };
      }

      return event;
    },
  });

  console.log(`✅ Client Sentry initialized for ${environment} environment`);
  if (environment === 'unknown-browser') {
    console.warn(
      `⚠️ Client Sentry: Environment detection failed, using fallback context:`,
      browserContext,
    );
  }

  // Global error handler for uncaught errors
  window.onerror = (message, source, lineno, colno, error) => {
    console.error('🚨 UNCAUGHT ERROR:', message, source, lineno, colno);
    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'uncaught_error');
      scope.setTag('source', source || 'unknown');
      scope.setExtra('line', lineno);
      scope.setExtra('column', colno);
      Sentry.captureException(error || new Error(String(message)));
    });
  };

  // Global handler for unhandled promise rejections (e.g., failed fetches)
  window.onunhandledrejection = (event) => {
    console.error('🚨 UNHANDLED REJECTION:', event.reason);
    Sentry.withScope((scope) => {
      scope.setTag('error_type', 'unhandled_rejection');
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      Sentry.captureException(error);
    });
  };
}

// Unregister any existing service workers to prevent caching issues
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister().then(() => {
        console.log('Service worker unregistered to prevent caching issues');
      });
    }
  });
}

// Capture Stripe checkout return params BEFORE React mounts.
// The success_url returns to /settings?subscription=success&session_id=cs_...
// but a client-side router redirect from /settings → /dashboard fires as
// soon as the doctor's profile hydrates (before useDoctorSubscription's
// enabled flag flips to true), stripping the query params. Stash them in
// sessionStorage so the hook can still find them after the redirect.
// See useDoctorSubscription.ts for consumption.
try {
  const params = new URLSearchParams(window.location.search);
  if (params.get('subscription') === 'success') {
    const sessionId = params.get('session_id');
    sessionStorage.setItem('subscription_return', 'success');
    if (sessionId) sessionStorage.setItem('subscription_session_id', sessionId);
  }
} catch {
  // sessionStorage may be blocked; hook falls back to URL-only path.
}

// Get Clerk publishable key from environment
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing Clerk Publishable Key');
}

createRoot(document.getElementById('root')!).render(
  <ErrorBoundary>
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      afterSignOutUrl="/auth"
    >
      <AuthProvider>
        <EncryptionProvider requireEncryption={true}>
          <App />
        </EncryptionProvider>
      </AuthProvider>
    </ClerkProvider>
  </ErrorBoundary>,
);
